import { FormEvent, useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCurrentUser } from '../../hooks/useCurrentUser';
import { authApi, readApiError } from '../../lib/auth-api';
import { AvatarUploader } from '../../components/AvatarUploader';
import { AppShell } from '../../components/AppShell';

/**
 * Account-level profile: avatar (with crop/upload), display name,
 * email + sign-in info. Workspace-level settings live under the
 * workspace settings page.
 */
export function ProfilePage() {
  const { t } = useTranslation();
  const { data: user, isLoading } = useCurrentUser();
  const qc = useQueryClient();

  const [name, setName] = useState('');
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (user) setName(user.name ?? '');
  }, [user]);

  const save = useMutation({
    mutationFn: () => authApi.updateProfile({
      name: name.trim() === '' ? undefined : name.trim(),
    }),
    onSuccess: (updated) => {
      qc.setQueryData(['currentUser'], updated);
      setSaved(true);
      setError(null);
      window.setTimeout(() => setSaved(false), 2400);
    },
    onError: (err) => setError(readApiError(err, 'Failed to save profile.')),
  });

  if (isLoading) {
    return (
      <AppShell>
        <main className="mx-auto max-w-2xl px-6 py-10">
          <p className="text-sm text-ink-500">{t('common.loading', { defaultValue: 'Loading…' })}</p>
        </main>
      </AppShell>
    );
  }
  if (!user) return <Navigate to="/" replace />;

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    save.mutate();
  };

  return (
    <AppShell>
      <main className="mx-auto max-w-2xl px-6 py-10 space-y-6">
        <div>
          <h1 className="text-2xl font-semibold text-ink-900 dark:text-ink-100 tracking-tight">
            {t('profile.title', { defaultValue: 'Profile' })}
          </h1>
          <p className="mt-1 text-sm text-ink-600 dark:text-ink-300">
            {t('profile.subtitle', { defaultValue: 'Your account, sign-in info, and how teammates see you.' })}
          </p>
        </div>

        <section className="rounded-2xl border border-ink-200 dark:border-ink-800 bg-white dark:bg-ink-900 p-6 space-y-5">
          <AvatarUploader user={user} />
          <div className="border-t border-ink-200 dark:border-ink-800 pt-3">
            <div className="text-sm text-ink-700 dark:text-ink-200 truncate">{user.email}</div>
            <div className="mt-1.5 flex items-center gap-3 text-[11px] uppercase tracking-[0.12em] text-ink-500">
              {user.isPlyEZStaff && (
                <span className="inline-flex items-center gap-1 text-accent-700">
                  <span className="inline-block h-1.5 w-1.5 rounded-full bg-accent-500" />
                  {t('profile.site_admin', { defaultValue: 'Site admin' })}
                </span>
              )}
              <span className="text-ink-500">{t(`profile.role_${user.role}`, { defaultValue: user.role })}</span>
            </div>
          </div>
        </section>

        <form onSubmit={onSubmit} className="rounded-2xl border border-ink-200 dark:border-ink-800 bg-white dark:bg-ink-900 p-6 space-y-4">
          <h2 className="text-base font-semibold text-ink-900 dark:text-ink-100">
            {t('profile.basics', { defaultValue: 'Basics' })}
          </h2>
          <div>
            <label htmlFor="display-name" className="block text-sm font-medium text-ink-900 dark:text-ink-100 mb-1">
              {t('profile.display_name', { defaultValue: 'Display name' })}
            </label>
            <input
              id="display-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={120}
              className="w-full h-10 rounded-lg border border-ink-200 bg-white px-3 text-sm text-ink-900 focus:outline-none focus:ring-2 focus:ring-accent-500/30 focus:border-accent-500 dark:bg-ink-950 dark:border-ink-700 dark:text-ink-100"
            />
            <p className="mt-1 text-[12px] text-ink-500 dark:text-ink-400">
              {t('profile.display_name_hint', { defaultValue: 'How teammates see you in mentions, audit logs, and comments.' })}
            </p>
          </div>

          {error && (
            <div role="alert" className="rounded-lg border border-warn-500/30 bg-warn-500/[0.06] text-warn-600 px-3 py-2 text-[13px]">{error}</div>
          )}
          {saved && (
            <div role="status" className="rounded-lg border border-emerald-200 bg-emerald-50 text-emerald-700 px-3 py-2 text-[13px]">
              {t('profile.saved', { defaultValue: 'Saved.' })}
            </div>
          )}

          <div className="flex justify-end pt-2">
            <button
              type="submit"
              disabled={save.isPending}
              className="rounded-lg bg-accent-500 text-white px-4 py-2 text-sm font-medium hover:bg-accent-600 disabled:opacity-50 transition"
            >
              {save.isPending ? t('common.saving', { defaultValue: 'Saving…' }) : t('common.save_changes', { defaultValue: 'Save changes' })}
            </button>
          </div>
        </form>
      </main>
    </AppShell>
  );
}
